import Cookies from "js-cookie";
import { cloneFilterState, FilterState } from "./filter.types";
import { calculateDateRange } from "./filter.utils";

const FILTER_COOKIE_NAME = "global_filter_state";

const serializeFilterState = (filterState: FilterState): string => {
  return JSON.stringify({
    period: filterState.period,
    comparison: filterState.comparison,
    dateRange: {
      startDate: filterState.dateRange.startDate.toISOString(),
      endDate: filterState.dateRange.endDate.toISOString(),
    },
    customDateRange: filterState.customDateRange
      ? {
          startDate: filterState.customDateRange.startDate.toISOString(),
          endDate: filterState.customDateRange.endDate.toISOString(),
        }
      : undefined,
  });
};

const deserializeFilterState = (
  serializedState: string,
): FilterState | null => {
  try {
    const parsed = JSON.parse(serializedState);

    // Se o período não é "custom", recalcular as datas dinamicamente
    // para evitar usar datas antigas do cookie
    // calculateDateRange já está importado no topo do arquivo
    const dateRange =
      parsed.period === "custom" && parsed.customDateRange
        ? {
            startDate: new Date(parsed.customDateRange.startDate),
            endDate: new Date(parsed.customDateRange.endDate),
          }
        : calculateDateRange(parsed.period);

    return {
      period: parsed.period,
      comparison: parsed.comparison,
      dateRange,
      customDateRange: parsed.customDateRange
        ? {
            startDate: new Date(parsed.customDateRange.startDate),
            endDate: new Date(parsed.customDateRange.endDate),
          }
        : undefined,
    } as FilterState;
  } catch (error) {
    console.error("Erro ao deserializar estado dos filtros:", error);
    // Remover cookie corrompido para evitar erro recorrente
    try {
      Cookies.remove(FILTER_COOKIE_NAME);
    } catch (removeError) {
      console.error(
        "Erro ao remover cookie de filtros corrompido:",
        removeError,
      );
    }
    return null;
  }
};

export const loadPersistedFilters = (): FilterState | null => {
  const savedFilterState = Cookies.get(FILTER_COOKIE_NAME);
  if (!savedFilterState) {
    return null;
  }

  return deserializeFilterState(savedFilterState);
};

export const persistFilters = (filterState: FilterState) => {
  const serializedState = serializeFilterState(filterState);
  Cookies.set(FILTER_COOKIE_NAME, serializedState, {
    expires: 365 * 10,
    secure: true,
    sameSite: "strict",
  });
};

export const clearPersistedFilters = (fallback: FilterState) => {
  Cookies.remove(FILTER_COOKIE_NAME);
  persistFilters(cloneFilterState(fallback));
};

const hasPersistedFilters = (): boolean =>
  Boolean(Cookies.get(FILTER_COOKIE_NAME));
